import { useState } from "react";
import { buildersApi, ApiError } from "@/api";
import type { BuilderInviteResponse } from "@/types";
import { Button, TextField } from "@/components/ui";
import { FormDrawer } from "../jobs/FormDrawer";

/** Invite one person at a builder firm to the portal app. The email carries the
 *  app download links only (no sign-in link — same reasoning as the client
 *  invite); they sign in by entering this address in the app. Untick "Send
 *  invite email" to create the login silently (e.g. before an imported Moxo
 *  history has been checked). One portal login per email address. */
export function InvitePersonDrawer({
  builderId,
  builderName,
  defaultName = "",
  defaultEmail = "",
  onClose,
  onInvited,
}: {
  builderId: number;
  builderName: string;
  defaultName?: string;
  defaultEmail?: string;
  onClose: () => void;
  onInvited: (res: BuilderInviteResponse) => void;
}) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit() {
    if (saving) return;
    if (!email.trim()) {
      setError("Enter the person's email address.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const res = await buildersApi.invitePerson(builderId, {
        name: name.trim() || undefined,
        email: email.trim(),
        send_invite_email: sendEmail,
      });
      onInvited(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not invite this person.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={`Invite to app — ${builderName}`}
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>{sendEmail ? "Invite" : "Create login"}</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}
      <section className="job-drawer__section">
        <h3>Person</h3>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} hint="Shown on their chat messages" />
        <TextField label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} hint="They sign in by entering this address in the app" />
        <label className="lead-drawer__check">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
          Send the invite email now
        </label>
        <p className="job-drawer__muted">
          They will see the chat on every job this firm is attached to, and nothing else — no documents, no approvals.
        </p>
      </section>
    </FormDrawer>
  );
}
